import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Circle,
  Image,
  Flex
} from '@chakra-ui/react';
import { FaCoins } from 'react-icons/fa';
import BaseCardComponent from '../BaseCardComponent';

/**
 * UniversalScratchCard Component
 * 
 * A flexible component for rendering scratch cards based on the universal template.
 * This component ensures consistent styling and behavior across all game types.
 */
const UniversalScratchCard = ({ 
  config,
  isPreview = false,
  gameState = null,
  onReveal = null
}) => {
  // Extract theme and card configuration
  const { theme, card, title, description, prizes } = config;
  
  // Initialize cells state
  const totalCells = card.grid.rows * card.grid.columns;
  const [cells, setCells] = useState(
    Array(totalCells).fill().map((_, i) => ({ 
      id: i, 
      revealed: false,
      value: null,
      isWinning: false
    }))
  );
  
  // Update cells when gameState changes
  useEffect(() => {
    if (!gameState) return;
    
    const newCells = [...cells];
    
    // Update revealed cells
    gameState.revealedCells.forEach(index => {
      if (index < newCells.length) {
        newCells[index].revealed = true;
        newCells[index].value = gameState.cellValues[index];
        newCells[index].isWinning = gameState.winningCells?.includes(index);
      }
    });
    
    setCells(newCells);
  }, [gameState]);
  
  // Handle cell click (scratch)
  const handleCellClick = (index) => {
    if (isPreview || !gameState || gameState.status !== 'active') return;
    
    // If cell is already revealed, do nothing
    if (cells[index].revealed) return;
    
    // Call onReveal callback
    if (onReveal) {
      onReveal(index);
    }
    
    // Update cell state
    const newCells = [...cells];
    newCells[index].revealed = true;
    
    // In a real game, this would come from the backend
    if (!newCells[index].value && gameState.cellValues) {
      newCells[index].value = gameState.cellValues[index];
      newCells[index].isWinning = gameState.winningCells?.includes(index);
    }
    
    setCells(newCells);
  };
  
  // Render a cell
  const renderCell = (index) => {
    const cell = cells[index];
    const isRevealed = cell.revealed;
    
    return (
      <Box
        key={index}
        bg={theme.colors.cellBackground}
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.2)"
        borderRadius="md"
        height="65px"
        width="65px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor={!isPreview && !isRevealed ? "pointer" : "default"}
        onClick={() => handleCellClick(index)}
        position="relative"
        transition="all 0.2s"
        _hover={!isPreview && !isRevealed ? { transform: "scale(1.05)" } : {}}
      >
        {isRevealed ? (
          <Box
            width="80%"
            height="80%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="xl" color="white" fontWeight="bold">{cell.value || '0'}</Text>
          </Box>
        ) : (
          <Box 
            position="absolute" 
            top="0" 
            left="0" 
            right="0" 
            bottom="0" 
            bg={theme.colors.cellBackground}
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
          >
            <Text fontSize="3xl" color={theme.colors.accent}>{card.symbols.winning}</Text>
          </Box>
        )}
      </Box>
    );
  };
  
  // Render the grid
  const renderGrid = () => {
    return (
      <Box
        bg={`rgba(${theme.colors.primary === '#8B0000' ? '40, 0, 0' : '0, 40, 20'}, 0.8)`}
        borderRadius="md"
        p={3}
        width="100%"
        maxW="100%"
        minH="280px"
        mx="auto"
        my={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        border={theme.glassmorphism.border}
        boxShadow={theme.glassmorphism.boxShadow}
      >
        <Box
          width="100%"
          display="grid"
          gridTemplateColumns={`repeat(${card.grid.columns}, 1fr)`}
          gridTemplateRows={`repeat(${card.grid.rows}, 1fr)`}
          gap="10px"
          justifyItems="center"
          alignItems="center"
          maxW="300px"
          mx="auto"
        >
          {Array(card.grid.rows * card.grid.columns).fill().map((_, i) => (
            <Box key={i}>
              {renderCell(i)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };
  
  // Calculate total prize pool
  const totalPrizePool = prizes.tiers.reduce((total, tier) => {
    return total + (tier.amount * tier.count);
  }, 0);
  
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      bg={theme.colors.primary}
      color={theme.colors.text}
      boxShadow="lg"
      position="relative"
      width="100%"
      maxWidth="375px"
      transition="transform 0.3s"
      _hover={{ transform: isPreview ? 'scale(1.02)' : 'none' }}
    >
      {isPreview ? (
        // Preview mode - show card info
        <>
          {/* Card Header */}
          <Box p={4} bg={`${theme.colors.secondary}`} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
            <Heading size="md" fontWeight="bold" mb={1}>{title || 'Custom Game'}</Heading>
            <Text fontSize="sm" noOfLines={2}>{description || 'A custom scratch card game'}</Text>
          </Box>
          
          {/* Card Body */}
          <VStack p={4} align="stretch" spacing={3}>
            {/* Prize Info */}
            <HStack justify="space-between">
              <Box px={2} py={1} bg="green.500" borderRadius="md" fontSize="xs">Custom Game</Box>
              <Box px={2} py={1} bg="purple.500" borderRadius="md" fontSize="xs">
                {config.publishedAt ? new Date(config.publishedAt).toLocaleDateString() : 'New'}
              </Box>
            </HStack>
            
            <Flex align="center" mt={2}>
              <FaCoins color={theme.colors.accent} />
              <Text ml={2} fontWeight="bold">
                Prize Pool: {totalPrizePool.toLocaleString()} {prizes.tokenType}
              </Text>
            </Flex>
          </VStack>
        </>
      ) : (
        // Game mode - show scratch card grid
        <>
          {/* Card Header */}
          <Box p={4} bg={theme.colors.secondary} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
            <Heading size="lg" fontWeight="bold" mb={1} color={theme.colors.accent} textAlign="center">{title || 'Custom Game'}</Heading>
            <Text fontSize="md" textAlign="center" color={theme.colors.text}>Win up to {Math.max(...prizes.tiers.map(t => t.amount)).toLocaleString()} {prizes.tokenType}</Text>
          </Box>
          
          {/* Winning Symbols */}
          <Box textAlign="center" mt={2} mb={2}>
            <Text fontSize="xl" color="cyan.300" mb={2}>Winning Symbols</Text>
            <Flex justifyContent="center" gap={2}>
              <Circle size="50px" bg={theme.colors.cellBackground} border="2px solid rgba(255, 255, 255, 0.3)">
                <Text fontSize="2xl" color={theme.colors.accent}>{card.symbols.winning}</Text>
              </Circle>
              <Circle size="50px" bg={theme.colors.cellBackground} border="2px solid rgba(255, 255, 255, 0.3)">
                <Text fontSize="2xl" color={theme.colors.accent}>{card.symbols.winning}</Text>
              </Circle>
              <Circle size="50px" bg={theme.colors.cellBackground} border="2px solid rgba(255, 255, 255, 0.3)">
                <Text fontSize="2xl" color={theme.colors.accent}>{card.symbols.winning}</Text>
              </Circle>
            </Flex>
          </Box>
          
          {/* Scratch Card Grid */}
          {renderGrid()}
          
          {/* Instructions */}
          <Box p={4} textAlign="center">
            <Text fontSize="lg" color="white">Match any of the winning symbols to win their prize value.</Text>
          </Box>
        </>
      )}
    </Box>
  );
};

export default UniversalScratchCard;
